import { debounce } from '@utilities/theme';

/**
 * layout-height-observer.js — site-wide ES module (loaded via <script type="module">).
 *
 * Tracks the rendered heights of #site-header and #site-footer and exposes them as
 * CSS custom properties (--header-height, --footer-height) on document.body, so layout
 * across the theme (drawers, facets, heroes, scroll offsets) can position against the
 * live header/footer size.
 *
 * #site-header wraps the full top stack (app banner + announcement bar + header group),
 * so --header-height reflects the whole stack and updates automatically when the app
 * banner collapses/expands, on font load, and on resize — no scroll polling needed.
 *
 * Public contracts consumed elsewhere (do not change without updating consumers):
 *   - body CSS vars --header-height / --footer-height
 *   - `header-height:change` CustomEvent ({ detail: { height } })
 *   - window.theme.header.height  (theme.header is seeded in theme-utilities.liquid)
 *
 * Extracted from the former global.js during the effort to remove that classic script.
 */

/**
 * Set a body-level height CSS variable, dispatching header-height:change and updating
 * window.theme.header.height when the header variable changes.
 * @param {string} propertyName - CSS custom property name (e.g. '--header-height')
 * @param {string} newValue - New value including unit (e.g. '120px')
 */
function updateHeightVariable(propertyName, newValue) {
  const currentValue = document.body.style.getPropertyValue(propertyName);
  if (currentValue === newValue) return;
  document.body.style.setProperty(propertyName, newValue);
  if (propertyName === '--header-height') {
    const parsedHeight = parseFloat(newValue) || 0;
    if (window.theme && window.theme.header) {
      window.theme.header.height = parsedHeight;
    }
    document.dispatchEvent(new CustomEvent('header-height:change', {
      detail: { height: parsedHeight }
    }));
  }
}

/**
 * Measure an element's current offsetHeight and write it to a body CSS variable.
 * @param {string} elementId - Element id to measure
 * @param {string} propertyName - CSS custom property to set
 */
function setElementHeightVariable(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;
  const height = `${element.offsetHeight}px`;
  updateHeightVariable(propertyName, height);
}

function updateAllHeightVariables() {
  setElementHeightVariable('site-header', '--header-height');
  setElementHeightVariable('site-footer', '--footer-height');
}

/**
 * ResizeObserver-based height tracking for dynamic elements.
 * More efficient than scroll-based polling - only fires when dimensions actually change.
 * Automatically handles:
 * - App banner collapse/expand on scroll (CSS Grid changes height)
 * - Window resize
 * - Dynamic content changes
 * - Font loading affecting layout
 */
const heightObserverCache = new Map();

function observeElementHeight(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;

  // Initial measurement
  setElementHeightVariable(elementId, propertyName);

  // Create ResizeObserver for this element
  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      // Use borderBoxSize for accurate height including padding/border
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight;
      const newValue = `${height}px`;

      // Only update if value changed (avoids unnecessary style recalcs)
      updateHeightVariable(propertyName, newValue);
    }
  });

  observer.observe(element);
  heightObserverCache.set(elementId, observer);

  return observer;
}

// Initialize height observers after DOM is ready
// Use requestIdleCallback for non-blocking initialization, fallback to setTimeout
const initHeightObservers = () => {
  observeElementHeight('site-header', '--header-height');
  observeElementHeight('site-footer', '--footer-height');
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(initHeightObservers, { timeout: 1000 });
} else {
  setTimeout(initHeightObservers, 500);
}

// Keep window resize listener as fallback for elements not observed
// and for any other components that might depend on resize events
const debouncedUpdateHeightVariables = debounce(updateAllHeightVariables, 250);
window.addEventListener('resize', debouncedUpdateHeightVariables);
